import type { ReactNode } from 'react'
import type { SessionView } from '~/helpers/learner/session-view'
import type { TrainingSheet } from '~/helpers/sessions/training-sheet'
import { ArrowLeft, CheckCircle2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { Button } from '~/components/ui/button'
import { formatMinutes, remainingMinutes } from '~/helpers/learner/duration'
import { completionStatus, nextStep, steps } from '~/helpers/learner/session-view'
import { tourAnchor } from '~/helpers/tour/anchors'
import ModuleArtwork from '~/pages/scorm/components/ModuleArtwork'
import PathFocus from '~/pages/scorm/components/PathFocus'
import PathHeader from '~/pages/scorm/components/PathHeader'
import PathSteps from '~/pages/scorm/components/PathSteps'
import TrainingSheetCard from '~/pages/scorm/components/TrainingSheetCard'

/** Ce que l'écran lit d'un parcours, au-delà de ses étapes. */
export type PathSession = SessionView & TrainingSheet & {
  progressionMode?: string | null
  myEnrolledAt?: unknown
}

type IProps = {
  session: PathSession
  /** Ouvre un module du parcours ; la démo du centre d'aide n'en ouvre aucun. */
  onLaunch: (moduleId: string) => void
  onDownloadCertificate: () => void
  downloadingCertificate: boolean
  /** Les documents du parcours (#326), à lire avant tout le reste. */
  documents?: ReactNode
  /** L'invitation à se positionner (#321), sous la fiche formation qui en donne les objectifs. */
  positioning?: ReactNode
}

/**
 * Le contenu de l'écran d'un parcours (#74), sans ses données : `SessionPath`
 * le nourrit de `mySessions`, la démo du centre d'aide de données fixes (#105).
 * Une seule vue pour les deux, donc une visite qui ne peut pas mentir.
 */
function SessionPathView({ session, onLaunch, onDownloadCertificate, downloadingCertificate, documents, positioning }: IProps) {
  const { t } = useTranslation()

  const all = steps(session)
  const done = session.progress?.completedModules ?? 0
  const total = session.progress?.totalModules ?? all.length
  const completed = completionStatus(session) === 'COMPLETED'
  const next = nextStep(session)
  // Ce que montre le panneau principal : l'étape à reprendre, ou à défaut la
  // première fermée — c'est elle qui explique pourquoi on s'arrête là.
  const focus = next ?? all.find(s => s.unlocked === false) ?? all.at(-1) ?? null
  const remaining = formatMinutes(remainingMinutes(all), t)

  return (
    <>
      <Button asChild variant="ghost" size="sm" className="mb-4 -ml-2">
        <Link to="/my-sessions">
          <ArrowLeft className="mr-2 size-4" />
          {t('path.back')}
        </Link>
      </Button>

      <PathHeader
        session={session}
        done={done}
        total={total}
        remaining={remaining}
        onDownloadCertificate={onDownloadCertificate}
        downloadingCertificate={downloadingCertificate}
      />

      <TrainingSheetCard sheet={session} />

      {documents}
      {positioning}

      {/*
        Le panneau du module en cours vient EN PREMIER, dans le DOM comme à
        l'écran sur téléphone : c'est ce qu'on vient faire. Sur grand écran, les
        étapes reprennent la colonne de gauche, collante. L'ordre du DOM suit
        celui qu'on lit sur mobile (RGAA 10.3) : le clavier n'y saute plus des
        étapes au panneau du haut.
      */}
      <div className="grid gap-6 lg:grid-cols-[20rem_1fr]">
        <section className="overflow-hidden rounded-2xl border border-border bg-card" {...tourAnchor('path.focus')}>
          {focus?.module?.id && (
            <ModuleArtwork moduleId={focus.module.id} className="h-32 w-full sm:h-40" />
          )}

          <div className="flex flex-col gap-4 p-5 sm:p-6">
            {completed
              ? (
                  <>
                    <p className="flex items-center gap-2 text-sm text-muted-foreground">
                      <CheckCircle2 className="size-4 text-success-text" />
                      {t('home.allStepsDone')}
                    </p>
                    <h2 className="font-heading text-xl leading-tight text-balance">
                      {t('path.done.title')}
                    </h2>
                    <p className="text-sm text-muted-foreground">{t('path.done.hint')}</p>
                  </>
                )
              : focus
                ? (
                    <PathFocus
                      step={focus}
                      steps={all}
                      total={total}
                      sections={session.sections ?? []}
                      onLaunch={onLaunch}
                    />
                  )
                : null}
          </div>
        </section>
        <aside className="lg:sticky lg:top-24 lg:order-first lg:self-start" {...tourAnchor('path.steps')}>
          <h2 className="mb-3 font-heading text-sm uppercase tracking-wide text-muted-foreground">
            {t('path.steps.title')}
          </h2>
          <PathSteps
            steps={all}
            sections={session.sections ?? []}
            currentId={focus?.module?.id}
            onLaunch={onLaunch}
          />
        </aside>
      </div>
    </>
  )
}

export default SessionPathView
